import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowRight, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface ApiRequestFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type?: 'api_key' | 'documentation' | 'integration_pack';
}

export function ApiRequestForm({ open, onOpenChange, type = 'integration_pack' }: ApiRequestFormProps) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    message: '',
  });

  const isApiKey = type === 'api_key';
  const isPack = type === 'integration_pack';
  const title = isPack
    ? 'Richiedi Integration Pack'
    : isApiKey
      ? 'Richiedi API Key'
      : 'Richiedi Documentazione API';
  const description = isPack
    ? 'Compili il modulo: il nostro team invierà direttamente all\'IT della sua struttura il pacchetto completo (chiavi API Sandbox e Live, endpoint REST e FHIR R4, snippet cURL/Python/JS, link SDK e guida). La Sandbox è inclusa gratuitamente per testare l\'integrazione senza toccare i dati di produzione.'
    : isApiKey
      ? 'Compili il modulo per ricevere una API Key per integrare Refertius nel suo sistema.'
      : 'Compili il modulo per ricevere la documentazione tecnica delle API Refertius.';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) {
      toast.error('Compili tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      const subject = isPack
        ? `Richiesta Integration Pack - ${form.organization}`
        : isApiKey
          ? `Richiesta API Key - ${form.organization}`
          : `Richiesta Documentazione API - ${form.organization}`;

      const body = `
        <h3>${title}</h3>
        <table style="border-collapse:collapse;width:100%">
          <tr><td style="padding:8px;font-weight:bold;">Nome:</td><td style="padding:8px;">${form.name}</td></tr>
          <tr><td style="padding:8px;font-weight:bold;">Struttura:</td><td style="padding:8px;">${form.organization}</td></tr>
          <tr><td style="padding:8px;font-weight:bold;">Email:</td><td style="padding:8px;">${form.email}</td></tr>
          ${form.phone ? `<tr><td style="padding:8px;font-weight:bold;">Telefono:</td><td style="padding:8px;">${form.phone}</td></tr>` : ''}
          ${form.message ? `<tr><td style="padding:8px;font-weight:bold;">Note:</td><td style="padding:8px;">${form.message}</td></tr>` : ''}
        </table>
      `;

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: 'Giorgio.arghittu@refertius.com',
          subject,
          type: 'custom',
          data: { customHtml: body },
        },
      });

      if (error) throw error;

      toast.success('Richiesta inviata con successo! La contatteremo al più presto.');
      onOpenChange(false);
      setForm({ name: '', organization: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Error sending API request:', err);
      toast.error('Errore nell\'invio. Riprovi o scriva a Giorgio.arghittu@refertius.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="req-name">Nome e Cognome *</Label>
            <Input
              id="req-name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Dr. Mario Rossi"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="req-org">Struttura / Azienda *</Label>
            <Input
              id="req-org"
              value={form.organization}
              onChange={(e) => setForm(f => ({ ...f, organization: e.target.value }))}
              placeholder="Clinica San Marco"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="req-email">Email *</Label>
            <Input
              id="req-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="mario.rossi@clinica.it"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="req-phone">Telefono</Label>
            <Input
              id="req-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+39 02 1234567"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="req-message">Note aggiuntive</Label>
            <Textarea
              id="req-message"
              value={form.message}
              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder={isPack ? 'Indichi gestionale in uso (Dedalus, GPI, custom...) ed email IT della struttura' : isApiKey ? 'Descriva brevemente il sistema da integrare...' : 'Specifichi le aree di interesse...'}
              rows={3}
            />
          </div>
          <Button type="submit" className="w-full gap-2" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Invia Richiesta
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
